import { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import AppButton from "../button/AppButton";
import AppTextInput from "../inputs/AppTextInput";
import AddressAutocompleteInput from "../inputs/AddressAutocompleteInput";
import { CreatePropertyRequest } from "../../models/RequestModels/CreatePropertyRequest";
import { useTranslation } from "../../translations/i18n";

interface PropertyFormModalProps {
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onSubmit: (body: CreatePropertyRequest) => void;
}

const PropertyFormModal = ({
  open,
  loading,
  onClose,
  onSubmit,
}: PropertyFormModalProps) => {
  const { t } = useTranslation();

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [notes, setNotes] = useState("");

  const resetForm = () => {
    setName("");
    setAddress("");
    setCity("");
    setNotes("");
  };

  const handleClose = () => {
    if (loading) {
      return;
    }

    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      return;
    }

    onSubmit({
      name: name.trim(),
      address: address.trim() || null,
      city: city.trim() || null,
      notes: notes.trim() || null,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>
        {t("properties.newProperty")}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <AppTextInput
            label={t("properties.propertyName")}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("properties.newPropertyPlaceholder")}
            disabled={loading}
            required
            autoFocus
          />

          <AddressAutocompleteInput
            label={t("properties.address")}
            value={address}
            onChange={setAddress}
            placeholder={t("properties.addressPlaceholder")}
            disabled={loading}
          />

          <AppTextInput
            label={t("properties.city")}
            value={city}
            onChange={(event) => setCity(event.target.value)}
            placeholder={t("properties.cityPlaceholder")}
            disabled={loading}
          />

          <AppTextInput
            label={t("properties.notes")}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder={t("properties.notesPlaceholder")}
            disabled={loading}
            multiline
            minRows={3}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <AppButton
          title={t("common.cancel")}
          variant="outlined"
          color="inherit"
          fullWidth={false}
          disabled={loading}
          onClick={handleClose}
        />

        <AppButton
          title={t("properties.createProperty")}
          fullWidth={false}
          loading={loading}
          disabled={!name.trim()}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default PropertyFormModal;
